import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, List, Typography } from '@mui/material';
import { PropertyListItem } from '@atoms';
import { FullWidthContent, MainMenuLayout, BoxContainer } from '@layouts';
import { GoBackButton } from '@molecules';
import { useProvinceFromArgentine } from '@modules/clients/data/PlacesRepository';
import { ADDRESS_ITEMS, COMPANY_ITEMS } from './constants';
import useGetPendingCustomerById from '../data/useGetPendingCustomerById';
import { PendingCustomer } from '../data/useGetPendingCustomers';

/**
 * Pending customer detail screen
 */
const PendingCustomerDetailScreen: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { customer, isLoading } = useGetPendingCustomerById(id);
  const { data: provinces } = useProvinceFromArgentine();

  const provinceName = useMemo(() => {
    if (!customer?.address?.stateId || !provinces) {
      return undefined;
    }
    return provinces.find((province) => province.id === customer.address.stateId)?.name;
  }, [customer?.address?.stateId, provinces]);

  /**
   * Navigates back to the pending customers list
   */
  const goBack = () => {
    navigate('/altas/gestion-razones-sociales');
  };

  /**
   * Renders a property list section
   * @param title - Section title i18n key
   * @param items - Detail items to render
   * @param province - Optional resolved province name
   */
  const renderSection = (
    title: string,
    items: typeof COMPANY_ITEMS,
    customerData: PendingCustomer,
    province?: string
  ) => (
    <Box mb={3}>
      <Typography variant='subtitle2' mb={1}>
        {t(title)}
      </Typography>
      <List>
        {items.map(({ label, getText }) => {
          const value = getText(customerData, province);
          return (
            <PropertyListItem
              key={label}
              label={label}
              value={
                value || (
                  <Typography sx={{ fontStyle: 'italic', fontSize: 15, color: 'neutral.400' }}>
                    {t('COMMON:NO_INFORMATION')}
                  </Typography>
                )
              }
            />
          );
        })}
      </List>
    </Box>
  );

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <BoxContainer>
          <Typography variant='h4' mb={3}>
            {t('PENDING_CUSTOMERS:DETAIL:TITLE')}
          </Typography>
          {isLoading ? (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={48} />
            </Box>
          ) : customer ? (
            <>
              {renderSection('PENDING_CUSTOMERS:DETAIL:COMPANY_DATA', COMPANY_ITEMS, customer)}
              {renderSection(
                'CLIENTS:DETAIL:POSTAL_ADDRESS:TITLE',
                ADDRESS_ITEMS,
                customer,
                provinceName
              )}
            </>
          ) : (
            <Alert variant='filled' severity='error'>
              {t('PENDING_CUSTOMERS:DETAIL:NOT_FOUND')}
            </Alert>
          )}
        </BoxContainer>
        <GoBackButton
          variant='outlined'
          text='COMMON:GO_BACK'
          onClick={() => {}}
          backFunction={goBack}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default PendingCustomerDetailScreen;
